import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import {
  ORDER_DETAIL_PAGE_VIEW_VALUES,
  type OrderDetailPageView,
} from '@/modules/Orders/OrderDetail/constants/orderDetailPageView.constants';

interface IHqPageViewContext {
  pageView: OrderDetailPageView;
  setPageView: (view: OrderDetailPageView) => void;
  isParticipantView: boolean;
}

const HqPageViewContext = createContext<IHqPageViewContext | null>(null);

interface IHqPageViewProviderProps {
  children: ReactNode;
  initialPageView?: OrderDetailPageView;
}

export const HqPageViewProvider: React.FC<IHqPageViewProviderProps> = ({
  children,
  initialPageView = ORDER_DETAIL_PAGE_VIEW_VALUES.DEFAULT,
}) => {
  const [pageView, setPageViewState] = useState<OrderDetailPageView>(initialPageView);

  const setPageView = useCallback((nextView: OrderDetailPageView) => {
    setPageViewState((current) => (current === nextView ? current : nextView));
  }, []);

  const value = useMemo(
    () => ({
      pageView,
      setPageView,
      isParticipantView: pageView === ORDER_DETAIL_PAGE_VIEW_VALUES.PARTICIPANT,
    }),
    [pageView, setPageView]
  );

  return <HqPageViewContext.Provider value={value}>{children}</HqPageViewContext.Provider>;
};

export const useHqPageView = (): IHqPageViewContext => {
  const ctx = useContext(HqPageViewContext);

  if (!ctx) {
    throw new Error('useHqPageView must be used within HqPageViewProvider');
  }

  return ctx;
};

HqPageViewProvider.displayName = 'HqPageViewProvider';
